import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar from '@/components/feature/FilterBar';
import MovieGrid from '@/components/feature/MovieGrid';
import Pagination from '@/components/feature/Pagination';
import {
  applyFilter,
  categoryMeta,
  defaultFilter,
  getByType,
  PAGE_SIZE,
  type CategorySlug,
  type FilterState,
} from '@/utils/movieUtils';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const validTypes: CategorySlug[] = ['movie', 'series', 'short'];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const slug = (validTypes.includes(type as CategorySlug) ? type : 'movie') as CategorySlug;
  const meta = categoryMeta[slug];

  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter });
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: `${meta.label}分类 - 午夜视频${meta.label}资源精选`,
    description: `午夜视频${meta.label}频道，${meta.desc}按地区、年份与热度筛选，快速找到好看的${meta.label}。`,
    keywords: `午夜视频,${meta.label},${meta.en},影视资源,${meta.label}推荐`,
  });

  useEffect(() => {
    setFilter({ ...defaultFilter });
    setPage(1);
  }, [slug]);

  const source = useMemo(() => getByType(slug), [slug]);
  const filtered = useMemo(() => applyFilter(source, filter), [source, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilter = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const handlePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 380, behavior: 'smooth' });
  };

  const invalid = !validTypes.includes(type as CategorySlug);

  if (invalid) {
    return (
      <div className="pt-16 md:pt-20 min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-600">请从下方入口选择你感兴趣的影视分类。</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-11 px-6 items-center rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20">
      <section className="border-b border-background-200 bg-background-100">
        <div className="px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${meta.label}` }]} />
          <div className="mt-4 flex items-center gap-4">
            <span className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
              <i className={`${meta.icon} text-3xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {meta.label}
                <span className="ml-2 text-sm font-normal text-foreground-500">{meta.en}</span>
              </h1>
              <p className="mt-1 text-sm text-foreground-600 max-w-2xl">{meta.desc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-6 md:py-8">
        <FilterBar filter={filter} onChange={handleFilter} />

        <div className="mt-6 flex items-center justify-between gap-4">
          <p className="text-sm text-foreground-600">
            共找到 <span className="text-primary-400 font-semibold">{filtered.length}</span> 部{meta.label}
          </p>
          <div className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-sort-desc"></i>
            {filter.sort === 'latest' ? '按最新' : filter.sort === 'rating' ? '按评分' : '按热门'}
          </div>
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="mt-4">
              <MovieGrid items={pageItems} />
            </div>
            {totalPages > 1 && (
              <div className="mt-10">
                <Pagination page={page} totalPages={totalPages} onChange={handlePage} />
              </div>
            )}
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center text-center py-16 rounded-lg border border-background-200 bg-background-100">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">抱歉，未找到符合条件的影视内容</h2>
            <p className="mt-2 text-sm text-foreground-600">试试放宽筛选条件，或重置后重新浏览。</p>
            <button
              type="button"
              onClick={() => handleFilter({ ...defaultFilter })}
              className="mt-6 inline-flex h-11 px-6 items-center gap-2 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>
    </div>
  );
}